import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TdLoadingService } from '@covalent/core/loading';
import { truncate } from 'lodash';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { FlightLogSummaryViewModelService } from 'src/app/core/flight-log-view-model.service';
import { USER } from 'src/app/core/user.service';
import { FlightLogSummarySortableField } from 'src/app/shared/flight-log-api/flight-logs/flight-log-client.model';
import { FlightLogSummaryViewModel } from 'src/view-models/flight-log-view-model';
import { UserViewModel } from 'src/view-models/user-view-model';
import { MaintenanceHttpClient } from '../../flight-log-api/maintenance/maintenance-client.service';

const mxImageMaxNameLength = 40;
type StringOrEmpty = string | null | undefined;

export type FlightLogSummaryTableColumn =
  | 'flightTime'
  | 'flightTagNumber'
  | 'flightSummaryId'
  | 'status'
  | 'flightStatus'
  | 'flightType'
  | 'tailNumber'
  | 'pilotInCommand'
  | 'startingEndingHobbs'
  | 'totalFlightTime'
  | 'base'
  | 'totalLandings'
  | 'runOnLandings'
  | 'engine1'
  | 'engine2'
  | 'image'
  | 'actions';

export interface FlightLogSummaryTableSort {
  orderBy: FlightLogSummarySortableField;
  direction: 'asc' | 'desc' | '';
}
@Component({
  selector: 'fl-flight-log-summaries-table',
  templateUrl: './flight-log-summaries-table.component.html',
  styleUrls: ['./flight-log-summaries-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlightLogSummariesTableComponent implements OnInit, OnDestroy {
  public loadingKey = 'flight-log-summaries-table';
  @Input()
  public flightLogs: Observable<FlightLogSummaryViewModel[]>;

  public flightLogVms$: Observable<Observable<FlightLogSummaryViewModel>[]>;

  public _onDestroy$ = new Subject();

  @Input()
  public total = 0;

  @Input()
  public pageSize = 50;

  @Input()
  public pageSizeOptions: number[] = [10, 25, 50, 100];

  @Input()
  public columns: FlightLogSummaryTableColumn[];

  @Output()
  public page = new EventEmitter<PageEvent>();

  @Output()
  public sort = new EventEmitter<FlightLogSummaryTableSort>();

  @Output()
  public flightLogDeleted = new EventEmitter();

  @Output()
  public flightLogRestored = new EventEmitter();

  public flightLogImageNames$ = new BehaviorSubject<Map<string, StringOrEmpty>>(
    new Map<string, StringOrEmpty>(),
  );

  get displayedColumns() {
    return this.columns.filter((c) => {
      if (!this.features.flightTagNumber && c === 'flightTagNumber') {
        return false;
      }
      return true;
    });
  }

  get features() {
    return this._user.flightLogFeatures;
  }

  get mxImageMap$(): Observable<Map<string, StringOrEmpty>> {
    return this.flightLogImageNames$.asObservable();
  }

  constructor(
    @Inject(USER) private _user: UserViewModel,
    private _loading: TdLoadingService,
    private _flightLogVm: FlightLogSummaryViewModelService,
    private _maintenance: MaintenanceHttpClient,
    private _snackBar: MatSnackBar,
  ) {}

  public markAsLoading() {
    this._loading.register(this.loadingKey);
  }

  public markAsLoaded() {
    this._loading.resolveAll(this.loadingKey);
  }

  public ngOnInit() {
    this.markAsLoading();
    this.flightLogVms$ = this.flightLogs.pipe(
      tap(() => this.markAsLoaded()),
      map((fls) => fls.map((fl) => this._flightLogVm.tryGetFromStore(fl))),
    );
    this.flightLogs
      .pipe(
        switchMap((fls) =>
          this._maintenance.getFlightLogMaintenanceImageNamesByIds(fls.map((f) => f.id)),
        ),
        takeUntil(this._onDestroy$),
      )
      .subscribe((res) => {
        const newImageMap = new Map(this.flightLogImageNames$.getValue());
        res.map((data: any) => {
          newImageMap.set(data.id, data.fileName);
        });
        this.flightLogImageNames$.next(newImageMap);
      });
  }

  public onFlightLogDeleted() {
    this.flightLogDeleted.emit();
  }

  public onFlightLogRestored() {
    this.flightLogRestored.emit();
  }

  public onPage(pageEvent: PageEvent) {
    this.page.emit(pageEvent);
  }

  public onSortChange({
    active,
    direction,
  }: {
    active: FlightLogSummarySortableField;
    direction: 'asc' | 'desc' | '';
  }) {
    this.sort.emit({
      orderBy: active,
      direction,
    });
  }

  public ngOnDestroy() {
    this._onDestroy$.next();
  }

  public viewImage(flightLogId: string, flightLogNumber: string, imageName: string) {
    if (flightLogId) {
      const imageNameTruncated = truncate(imageName, {
        omission: '...',
        length: mxImageMaxNameLength,
      });
      this._snackBar.open(
        `Downloading "${imageNameTruncated}" for flight ${flightLogNumber}...`,
        undefined,
      );
      try {
        this._maintenance
          .getFlightLogMaintenanceImageById(flightLogId)
          .pipe(take(1))
          .subscribe({
            next: (res) => {
              const uri = window.URL.createObjectURL(res.body as Blob);
              window.open(uri?.toString(), '_blank');
            },
            error: (err) => {
              const error = `${
                err.status === 404
                  ? `File not found or unavailable`
                  : `There was an error retrieving the image`
              } for flight ${flightLogNumber}.`;
              this._snackBar.open(error, undefined, { duration: 3000 });
            },
            complete: () => {
              setTimeout(() => {
                this._snackBar.dismiss();
              }, 3000);
            },
          });
      } catch (err) {
        const error = `There was an error retrieving the image for flight ${flightLogNumber}.`;
        this._snackBar.open(error, undefined, { duration: 3000 });
      }
    }
  }
}
